import { FeatureLimitsKeys, type PresetFilter } from '@/types'
import type {
  RecordStrategy,
  RecordStrategyArgs,
  SearchableData,
} from '../types'
import { isFeatureAvailable } from '@utils/helpers/planFeatureLimitHelper'
import { useSpace } from '@/store/modules/App/space'
import { useFilters } from 'src/store/modules/App/filters'
import { storeToRefs } from 'pinia'

const recordKey = 'Quick Filters'
export class QuickFiltersRecord implements RecordStrategy {
  async get({ spaceId }: RecordStrategyArgs): Promise<PresetFilter[]> {
    const { isBlockedOrUnpaid } = useSpace()

    if (!spaceId || isBlockedOrUnpaid) {
      return []
    }
    const filtersStore = useFilters()
    const { presetFilters } = storeToRefs(filtersStore)
    const { loadPresetFilters } = filtersStore

    // if there are no filters in the store because the page doesn't
    // have a /preset_filters request, we should load them
    if (typeof presetFilters.value === 'undefined') {
      await loadPresetFilters({ spaceId })
    }

    return presetFilters.value?.filter(({ is_favorite }) => is_favorite) ?? []
  }

  async set(args: RecordStrategyArgs = {}): Promise<void> {
    const { spaceId } = args
    if (!spaceId) {
      return
    }
    const quickFilterData = await this.get(args)

    window?.CommandBar?.addRecords(
      recordKey,
      this.createSearchableRecord(quickFilterData, spaceId),
    )
  }

  remove(): void {
    window?.CommandBar?.addRecords(recordKey, [])
  }

  createSearchableRecord(
    record: PresetFilter[],
    spaceId: string,
  ): SearchableData[] {
    let searchableRecord: SearchableData[] | [] = []
    const starredFilters: SearchableData[] = record.map(
      ({ id, name }: PresetFilter) => ({
        spaceId,
        id,
        title: 'name',
        label: name,
        description: `Space: ${useSpace().getSpaceName}`,
        url: `/#/me/spaces/${spaceId}/stories/0/0/index/0?presetFilterId=${id}&trigger=spotlight`,
      }),
    )

    searchableRecord = [...searchableRecord, ...starredFilters]

    const favoriteStoriesFilter = {
      spaceId,
      id: 'favourite',
      title: 'My favorites',
      label: 'My favorites',
      description: `Space: ${useSpace().getSpaceName}`,
      icon: 'https://a.storyblok.com/f/136045/24x24/b71a787490/my-favorite-stories-filter.svg',
      url: `/#/me/spaces/${spaceId}/stories/0/0/index/0?filter={"favourite":true,"has_filter":true}&currentPage=1&trigger=spotlight`,
    }

    if (isFeatureAvailable(FeatureLimitsKeys.CanHaveFavoriteStories)) {
      searchableRecord = [...searchableRecord, favoriteStoriesFilter]
    }

    return searchableRecord
  }
}
